import type { Instance } from "mobx-state-tree";
import { types, flow, cast } from "mobx-state-tree";
import * as repo from "./repository";
import { DesignDiscipline } from "./models";
import type { DesignDisciplineRow, DesignDisciplineSnapshotIn } from "./models";
import type { TablesInsert, TablesUpdate } from "@/types/supabase-helpers";

export const DesignDisciplinesStore = types
  .model("DesignDisciplinesStore", {
    disciplines: types.array(DesignDiscipline),
    isLoading: types.optional(types.boolean, false),
    error: types.maybeNull(types.string),
  })
  .views((self) => ({
    get disciplinesList() {
      return self.disciplines.slice();
    },
    getById(id: number) {
      return self.disciplines.find((d) => d.id === id);
    },
    getByName(name: string) {
      return self.disciplines.find((d) => d.name === name);
    },
  }))
  .actions((self) => ({
    loadDisciplines: flow(function* loadDisciplines() {
      self.isLoading = true;
      self.error = null;

      try {
        const data: DesignDisciplineRow[] = yield repo.listDesignDisciplines();
        self.disciplines.clear();
        data.forEach((d) => {
          self.disciplines.push(d as DesignDisciplineSnapshotIn);
        });
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      } finally {
        self.isLoading = false;
      }
    }),

    createDiscipline: flow(function* createDiscipline(
      discipline: TablesInsert<"design_disciplines">
    ) {
      self.error = null;
      try {
        const newDiscipline: DesignDisciplineRow = yield repo.createDesignDiscipline(discipline);
        self.disciplines.push(newDiscipline as DesignDisciplineSnapshotIn);
        return newDiscipline;
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    updateDiscipline: flow(function* updateDiscipline(
      id: number,
      updates: TablesUpdate<"design_disciplines">
    ) {
      self.error = null;
      try {
        const updatedDiscipline: DesignDisciplineRow = yield repo.updateDesignDiscipline(
          id,
          updates
        );
        const index = self.disciplines.findIndex((d) => d.id === id);
        if (index !== -1) {
          self.disciplines[index] = cast(updatedDiscipline as DesignDisciplineSnapshotIn);
        }
        return updatedDiscipline;
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    deleteDiscipline: flow(function* deleteDiscipline(id: number) {
      self.error = null;
      try {
        yield repo.deleteDesignDiscipline(id);
        const index = self.disciplines.findIndex((d) => d.id === id);
        if (index !== -1) {
          self.disciplines.splice(index, 1);
        }
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    clear() {
      self.disciplines.clear();
      self.isLoading = false;
      self.error = null;
    },
  }));

export type DesignDisciplinesStoreInstance = Instance<typeof DesignDisciplinesStore>;
